/**
 * AiDailyBriefWidget — AI-утренний брифинг для HR/руководителей.
 *
 * Дёргает GET /ai/daily-brief (Gemini-дайджест дня: открытые вакансии,
 * застрявшие кандидаты, истекающие испытательные сроки, зависшие офферы).
 * Рендерится только для HR/админ-ролей (гейт в HrFocusedDashboard).
 *
 * Самодостаточная карточка (НЕ через Widget): один внутренний паддинг вместо
 * двойного card__header+card__body (иначе ~48px пустой полосы под заголовком),
 * без разделителя, и собственный marginBottom — иначе блок «склеивается» с
 * KPI-рядом ниже (он сосед без отступа). См. frontend-design-system.mdc §21.
 */

import { useEffect, useState } from 'react'
import { Sparkles, ArrowRight, RefreshCw, CheckCircle2, TimerReset } from 'lucide-react'

import { apiGetDailyBrief } from '../../api/aiBrief'
import { seriesColor } from '../../utils/chartPalette'
import KpiTile from '../training/KpiTile'
import SparklineChart from '../training/SparklineChart'
import LoadingIcon from '../LoadingIcon'

/**
 * Метки — однородные именные группы (без глаголов): «Открытые вакансии», а не
 * «Застряли кандидаты».
 *
 * `worseWhen` — направление, которое для показателя означает ухудшение. Рост
 * открытых вакансий сам по себе не хорош и не плох (набор идёт), а рост
 * кандидатов без движения — всегда плохо. Без этого поля стрелка вверх красилась
 * бы красным там, где ничего не случилось.
 */
const SIGNAL_META = {
    open_vacancies: { label: 'Открытые вакансии', worseWhen: null },
    stuck_candidates: { label: 'Кандидаты без движения', worseWhen: 'up' },
    probations_ending: { label: 'Испыт. сроки на неделе', worseWhen: null },
    stale_offers: { label: 'Офферы без ответа', worseWhen: 'up' },
}

const SPARK_HEIGHT = 34

/** Тон плитки: тревожный показатель — статусный, нейтральный — без окраски числа. */
function signalTone(meta, value, delta) {
    if (!value || meta.worseWhen !== 'up') return undefined
    return delta > 0 ? 'danger' : 'warning'
}

/** Что изменение означает ДЛЯ ЭТОГО показателя: рост вакансий нейтрален, рост просрочек — плохо. */
function deltaMeaning(meta, delta) {
    if (!meta.worseWhen || !delta) return 'neutral'
    const worse = meta.worseWhen === 'up' ? delta > 0 : delta < 0
    return worse ? 'bad' : 'good'
}

/** Цвет линии истории: у тревожного показателя статусный, у нейтрального — цвет ряда графика. */
function historyColor(tone, index) {
    if (tone === 'danger') return 'var(--color-danger-text)'
    if (tone === 'warning') return 'var(--color-warning-text)'
    return seriesColor(index)
}

export default function AiDailyBriefWidget() {
    const [brief, setBrief] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [refreshing, setRefreshing] = useState(false)

    const load = async ({ force = false } = {}) => {
        try {
            if (force) setRefreshing(true)
            const data = await apiGetDailyBrief({ force })
            setBrief(data)
            setError('')
        } catch (e) {
            setError(e?.message || 'Не удалось загрузить брифинг')
        } finally {
            setLoading(false)
            setRefreshing(false)
        }
    }

    useEffect(() => {
        load()
    }, [])

    return (
        <div
            className="card card--elevated animate-rise-in"
            style={{ padding: '18px 20px', marginBottom: 20 }}
        >
            {/* Шапка: заголовок + Обновить в одной строке, без card__header-разделителя. */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
                <span className="card__title">AI-брифинг на сегодня</span>
                <button
                    type="button"
                    onClick={() => load({ force: true })}
                    disabled={refreshing || loading}
                    title="Пересчитать"
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--color-accent)', fontSize: 12, fontWeight: 600,
                    }}
                >
                    <RefreshCw size={12} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} aria-hidden="true" />
                    Обновить
                </button>
            </div>

            {loading ? (
                <div style={{ padding: '12px 0', textAlign: 'center' }}>
                    <LoadingIcon />
                </div>
            ) : error ? (
                <p className="text-rel-sm-2 text-muted" style={{ margin: 0 }}>{error}</p>
            ) : (
                <>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
                        <span
                            style={{
                                flexShrink: 0, width: 32, height: 32, borderRadius: 'var(--radius-md)',
                                background: 'var(--color-accent-bg)', color: 'var(--color-accent)',
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            }}
                        >
                            <Sparkles size={18} aria-hidden="true" />
                        </span>
                        <p style={{ margin: 0, fontWeight: 700, fontSize: 15, lineHeight: 1.4, color: 'var(--color-text)' }}>
                            {brief?.headline || 'Сводка на сегодня'}
                        </p>
                    </div>

                    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {(brief?.priorities || []).map((p, i) => (
                            <li key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                <ArrowRight size={15} style={{ flexShrink: 0, marginTop: 2, color: 'var(--color-accent)' }} aria-hidden="true" />
                                <span style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--color-text)' }}>{p}</span>
                            </li>
                        ))}
                    </ul>

                    {(brief?.action_cards || []).length > 0 && (
                        <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
                            {brief.action_cards.slice(0, 3).map((card, i) => (
                                <div
                                    key={`${card.decide_now || 'action'}-${i}`}
                                    style={{
                                        display: 'grid',
                                        gap: 6,
                                        padding: 10,
                                        borderRadius: 8,
                                        border: '1px solid var(--color-border)',
                                        background: 'var(--color-bg-elevated)',
                                    }}
                                >
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                        <CheckCircle2 size={15} style={{ flexShrink: 0, marginTop: 2, color: 'var(--color-success)' }} aria-hidden="true" />
                                        <strong style={{ fontSize: 13, lineHeight: 1.35, color: 'var(--color-text)' }}>
                                            {card.decide_now}
                                        </strong>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', color: 'var(--color-text-muted)', fontSize: 12, lineHeight: 1.35 }}>
                                        <TimerReset size={14} style={{ flexShrink: 0, marginTop: 1, color: 'var(--color-warning)' }} aria-hidden="true" />
                                        <span>{card.delay_risk}</span>
                                    </div>
                                    <span style={{ color: 'var(--color-accent)', fontSize: 12, fontWeight: 700 }}>
                                        {card.cta || 'Открыть задачу'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {brief?.signals && (
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                gap: 10, marginTop: 14, paddingTop: 12,
                                borderTop: '1px solid var(--color-border)',
                            }}
                        >
                            {Object.entries(SIGNAL_META).map(([key, meta], index) => {
                                const value = brief.signals[key] ?? 0
                                // Ответ без истории (старый бэкенд) — плитка живёт без спарклайна.
                                const trend = brief.signal_trends?.[key]
                                const delta = Number.isFinite(trend?.delta) ? trend.delta : null
                                const history = trend?.history || []
                                const tone = signalTone(meta, value, delta)
                                return (
                                    <KpiTile
                                        key={key}
                                        label={meta.label}
                                        value={value}
                                        tone={tone}
                                        sentenceLabel
                                        big
                                        // Нулевые сигналы приглушаем — фокус на том, где реально есть работа.
                                        dim={!value}
                                        delta={delta}
                                        deltaTone={deltaMeaning(meta, delta)}
                                        footer={history.length >= 2 && (
                                            <SparklineChart
                                                values={history}
                                                color={historyColor(tone, index)}
                                                height={SPARK_HEIGHT}
                                                showLast={false}
                                                stretch
                                                fill
                                            />
                                        )}
                                    />
                                )
                            })}
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
